import { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { toast } from "react-hot-toast";
import { Plus, Trash2 } from "lucide-react";

function BankEditor({ bank, onChange, onRemove }) {
  return (
    <div className="p-3 rounded-xl bg-slate-700 space-y-2">
      <div className="grid md:grid-cols-2 gap-2">
        <input
          className="px-3 py-2 rounded bg-slate-600 text-white"
          placeholder="Bank name"
          value={bank.bankName}
          onChange={(e) => onChange({ ...bank, bankName: e.target.value })}
        />
        <input
          className="px-3 py-2 rounded bg-slate-600 text-white"
          placeholder="Branch"
          value={bank.branch || ""}
          onChange={(e) => onChange({ ...bank, branch: e.target.value })}
        />
        <input
          className="px-3 py-2 rounded bg-slate-600 text-white"
          placeholder="Account name"
          value={bank.accountName}
          onChange={(e) => onChange({ ...bank, accountName: e.target.value })}
        />
        <input
          className="px-3 py-2 rounded bg-slate-600 text-white"
          placeholder="Account number"
          value={bank.accountNumber}
          onChange={(e) => onChange({ ...bank, accountNumber: e.target.value })}
        />
      </div>
      <input
        className="w-full px-3 py-2 rounded bg-slate-600 text-white"
        placeholder="Notes (optional)"
        value={bank.notes || ""}
        onChange={(e) => onChange({ ...bank, notes: e.target.value })}
      />
      <div className="flex justify-end">
        <button
          onClick={onRemove}
          className="px-3 py-2 rounded bg-red-500 text-white flex items-center gap-2 text-sm"
        >
          <Trash2 size={16} /> Remove
        </button>
      </div>
    </div>
  );
}

export default function AgentPaymentSettings({ user }) {
  const [banks, setBanks] = useState([]);
  const [binancePayId, setBinancePayId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      if (!user?.uid) return;
      const a = await getDoc(doc(db, "agents", user.uid));
      if (a.exists()) {
        const d = a.data();
        setBanks(d.banks || []);
        setBinancePayId(d.binancePayId || "");
      }
    })();
  }, [user?.uid]);

  const addBank = () => {
    setBanks((b) => [
      ...b,
      {
        bankName: "",
        branch: "",
        accountName: "",
        accountNumber: "",
        notes: "",
      },
    ]);
  };

  const updateBank = (idx, value) => {
    setBanks((b) => b.map((x, i) => (i === idx ? value : x)));
  };

  const removeBank = (idx) => {
    setBanks((b) => b.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!user?.uid) return toast.error("Login first");
    try {
      setLoading(true);
      await updateDoc(doc(db, "agents", user.uid), {
        banks,
        binancePayId,
        updatedAt: serverTimestamp(),
      });
      toast.success("Payment methods saved");
    } catch (e) {
      console.error(e);
      toast.error("Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto mt-10">
      <h1 className="text-2xl font-bold mb-4">Payment Settings</h1>

      <div className="p-4 rounded-2xl bg-slate-800">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Bank Accounts</h2>
          <button
            onClick={addBank}
            className="px-3 py-2 rounded bg-slate-700 text-sm flex items-center gap-2"
          >
            <Plus size={16} /> Add Bank
          </button>
        </div>

        <div className="space-y-3">
          {banks.map((b, i) => (
            <BankEditor
              key={i}
              bank={b}
              onChange={(v) => updateBank(i, v)}
              onRemove={() => removeBank(i)}
            />
          ))}
          {!banks.length && (
            <div className="text-sm text-slate-400">No bank accounts yet.</div>
          )}
        </div>

        <div className="mt-6">
          <h2 className="font-semibold mb-2">Binance Pay</h2>
          <input
            className="w-full px-3 py-2 rounded bg-slate-700 text-white"
            placeholder="Binance Pay ID (optional)"
            value={binancePayId}
            onChange={(e) => setBinancePayId(e.target.value)}
          />
          <div className="text-xs text-slate-400 mt-1">
            If you accept Binance, share your Pay ID here. Players will see it
            during payment.
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={save}
            disabled={loading}
            className="px-4 py-2 rounded bg-lime-400 text-black font-semibold disabled:opacity-50"
          >
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
